import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import store from './app/store';
import { Provider } from 'react-redux';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Suppress ResizeObserver loop error - this is a benign error that occurs
// when ResizeObserver callbacks are triggered during navigation/unmounting
// It doesn't affect functionality and is safe to ignore
const resizeObserverErr = window.onerror;
window.onerror = (message, ...args) => {
  if (message?.includes?.('ResizeObserver loop')) {
    return true; // Suppress the error
  }
  return resizeObserverErr?.(message, ...args);
};

// Also handle it in the error event listener
window.addEventListener('error', (e) => {
  if (e.message?.includes?.('ResizeObserver loop')) {
    e.stopPropagation();
    e.preventDefault();
  }
});

// Suppress ALL console output in production (except staging/preview)
// Live domains: app.userevstar.com, *.userevstar.com storefronts, etc.
// Staging: staging.userevstar.com (logs enabled for debugging)
// Preview: *.vercel.app (logs enabled for debugging)
if (process.env.NODE_ENV === 'production' &&
    !window.location.hostname.includes('staging') &&
    !window.location.hostname.includes('vercel.app') &&
    !window.location.hostname.includes('localhost')) {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.warn = noop;
  console.error = noop; // Also suppress errors in production for clean console
  console.trace = noop;
  console.table = noop;
}

// Google OAuth: when REACT_APP_GOOGLE_CLIENT_ID is set, we wire real OAuth.
// When unset (typical in local dev), we wrap with a placeholder client ID so
// that components calling useGoogleLogin/useGoogleOAuth still receive the
// provider context and render. The placeholder is invalid, so any actual
// click-through OAuth call will fail at the Google API layer (not at render),
// which is acceptable in dev — email/password signup still works.
const envGoogleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const hasRealGoogleClient = !!envGoogleClientId && !envGoogleClientId.startsWith('your-');
const googleClientId = hasRealGoogleClient
  ? envGoogleClientId
  : 'local-dev-placeholder.apps.googleusercontent.com';

if (!hasRealGoogleClient && process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line no-console
  console.warn(
    '[index] REACT_APP_GOOGLE_CLIENT_ID is not set. Google sign-in is disabled. ' +
    'Email/password signup works. Set REACT_APP_GOOGLE_CLIENT_ID in frontend/.env to enable Google.'
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <HelmetProvider>
    <Provider store={store}>
      <GoogleOAuthProvider clientId={googleClientId}>
        <App />
      </GoogleOAuthProvider>
    </Provider>
  </HelmetProvider>
);
